import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useRef, type DragEvent, type ChangeEvent } from "react";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  File as FileIcon,
  Hash,
  Hourglass,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";
import { AppShell } from "@client/components/app-shell";
import { Button } from "@client/components/ui/button";
import { Spinner } from "@client/components/ui/spinner";
import { cn } from "@client/lib/cn";
import { friendlyMime } from "@client/lib/mime";

export const Route = createFileRoute("/documents/upload")({
  beforeLoad: async () => {
    const res = await fetch("/api/me", { credentials: "include" });
    if (!res.ok) throw redirect({ to: "/login" });
  },
  component: UploadPage,
});

const ALLOWED_MIMES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "image/jpeg",
  "image/png",
];
const MAX_BYTES = 100 * 1024 * 1024;

const ACCEPTED_LABELS = "PDF · DOCX · XLSX · ODT · ODS · JPG · PNG — até 100 MB";

type Stage = "idle" | "hashing" | "requesting" | "uploading" | "finalizing" | "processing";

interface UploadState {
  stage: Stage;
  progress: number;
  documentId: string | null;
  error: string | null;
  fileName: string | null;
  fileSize: number | null;
  fileMime: string | null;
}

interface DocStatus {
  id: string;
  name: string;
  status: "pending" | "processing" | "ready" | "rejected" | "quarantined";
  downloadUrl: string | null;
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hashBuf = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function presignUpload(file: File) {
  const res = await fetch("/api/documents", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      name: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (res.status === 413) throw new Error("Arquivo maior que 100MB.");
    throw new Error(body?.error ?? `Erro ao iniciar upload (${res.status}).`);
  }
  return res.json() as Promise<{ id: string; uploadUrl: string }>;
}

function putWithProgress(url: string, file: File, onProgress: (p: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload falhou (${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error("Erro de rede no upload."));
    xhr.send(file);
  });
}

async function finalizeUpload(documentId: string, checksumSha256: string) {
  const res = await fetch(`/api/documents/${documentId}/finalize`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ checksumSha256 }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? `Erro ao finalizar (${res.status}).`);
  }
  return res.json();
}

async function fetchDoc(id: string): Promise<DocStatus> {
  const res = await fetch(`/api/documents/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error("Documento não encontrado");
  return res.json();
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fileIcon(mime: string | null) {
  if (!mime) return <FileIcon className="h-5 w-5 text-[var(--color-ink-muted)]" />;
  if (mime.startsWith("image/")) return <FileImage className="h-5 w-5 text-[var(--color-info)]" />;
  if (mime === "application/pdf")
    return <FileText className="h-5 w-5 text-[var(--color-danger)]" />;
  if (mime.includes("spreadsheet"))
    return <FileSpreadsheet className="h-5 w-5 text-[var(--color-success)]" />;
  if (mime.includes("word") || mime.includes("document"))
    return <FileType className="h-5 w-5 text-[var(--color-info)]" />;
  return <FileIcon className="h-5 w-5 text-[var(--color-ink-muted)]" />;
}

const PIPELINE: { key: Stage; label: string; description: string }[] = [
  { key: "hashing", label: "Preparando arquivo", description: "Verificando a integridade" },
  { key: "requesting", label: "Conectando", description: "Preparando envio seguro" },
  { key: "uploading", label: "Enviando", description: "Transferência criptografada" },
  { key: "finalizing", label: "Confirmando", description: "Validando o envio" },
  { key: "processing", label: "Verificação de segurança", description: "Análise antivírus" },
];

function stageIndex(s: Stage): number {
  return PIPELINE.findIndex((p) => p.key === s);
}

function UploadPage() {
  const [state, setState] = useState<UploadState>({
    stage: "idle",
    progress: 0,
    documentId: null,
    error: null,
    fileName: null,
    fileSize: null,
    fileMime: null,
  });
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: docStatus } = useQuery({
    queryKey: ["doc", state.documentId],
    queryFn: () => fetchDoc(state.documentId!),
    enabled: !!state.documentId && state.stage === "processing",
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (!s) return 2000;
      if (s === "ready" || s === "rejected" || s === "quarantined") return false;
      return 2000;
    },
  });

  async function handleFile(file: File) {
    setState({
      stage: "idle",
      progress: 0,
      documentId: null,
      error: null,
      fileName: file.name,
      fileSize: file.size,
      fileMime: file.type,
    });

    if (!ALLOWED_MIMES.includes(file.type)) {
      setState((s) => ({
        ...s,
        error: `Tipo "${file.type || "desconhecido"}" não permitido. Use ${ACCEPTED_LABELS}.`,
      }));
      return;
    }
    if (file.size > MAX_BYTES) {
      setState((s) => ({ ...s, error: "Arquivo maior que 100MB." }));
      return;
    }

    try {
      setState((s) => ({ ...s, stage: "hashing", progress: 0 }));
      const checksum = await sha256Hex(file);

      setState((s) => ({ ...s, stage: "requesting" }));
      const { id, uploadUrl } = await presignUpload(file);

      setState((s) => ({ ...s, stage: "uploading", documentId: id, progress: 0 }));
      await putWithProgress(uploadUrl, file, (p) => setState((s) => ({ ...s, progress: p })));

      setState((s) => ({ ...s, stage: "finalizing", progress: 100 }));
      await finalizeUpload(id, checksum);

      setState((s) => ({ ...s, stage: "processing" }));
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : "Erro desconhecido.",
      }));
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  function reset() {
    setState({
      stage: "idle",
      progress: 0,
      documentId: null,
      error: null,
      fileName: null,
      fileSize: null,
      fileMime: null,
    });
    if (inputRef.current) inputRef.current.value = "";
  }

  const finalStatus = docStatus?.status;
  const isDone =
    finalStatus === "ready" || finalStatus === "rejected" || finalStatus === "quarantined";
  const currentIdx = stageIndex(state.stage);
  const showPipeline = state.stage !== "idle" && !state.error;
  const showDropzone = state.stage === "idle" && !state.error;

  return (
    <AppShell
      pageEyebrow="Upload"
      pageTitle="Adicionar documento"
      pageActions={
        <Button asChild variant="ghost">
          <Link to="/dashboard">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Link>
        </Button>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5 min-w-0">
          {showDropzone && (
            <div
              className={cn(
                "relative rounded-lg border-2 border-dashed transition-all cursor-pointer overflow-hidden",
                "px-6 py-16 text-center bg-[var(--color-surface)]",
                dragOver
                  ? "border-[var(--color-gold)] bg-[var(--color-gold-wash)]"
                  : "border-[var(--color-line-strong)] hover:border-[var(--color-gold-dim)]",
              )}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
            >
              <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--color-gold-dim)] to-transparent opacity-60"
              />
              <div
                className={cn(
                  "mx-auto flex h-14 w-14 items-center justify-center rounded-md mb-5 transition-all",
                  dragOver
                    ? "bg-[var(--color-gold)] text-[var(--color-gold-ink)] scale-110"
                    : "bg-[var(--color-surface-3)] text-[var(--color-gold)] hairline",
                )}
              >
                <UploadCloud className="h-7 w-7" strokeWidth={1.8} />
              </div>
              <p className="text-title text-xl text-[var(--color-ink)]">
                Arraste um arquivo
                <span className="text-[var(--color-ink-muted)] font-normal"> ou </span>
                clique para selecionar
              </p>
              <p className="mt-2 text-sm text-[var(--color-ink-muted)]">{ACCEPTED_LABELS}</p>
              <p className="mt-5 text-xs text-[var(--color-ink-muted)]">
                Cada arquivo é verificado por antivírus antes de ficar disponível.
              </p>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept={ALLOWED_MIMES.join(",")}
                onChange={onPick}
              />
            </div>
          )}

          {state.error && (
            <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden">
              <div className="flex items-start gap-3 px-4 py-4 border-l-2 border-l-[var(--color-danger)]">
                <AlertCircle className="h-5 w-5 mt-0.5 text-[var(--color-danger)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--color-ink)]">
                    Não foi possível enviar
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{state.error}</p>
                </div>
              </div>
              <div className="hairline-t px-4 py-3 flex justify-end">
                <Button variant="secondary" size="sm" onClick={reset}>
                  <RefreshCw className="h-3.5 w-3.5" />
                  Tentar outro arquivo
                </Button>
              </div>
            </div>
          )}

          {showPipeline && (
            <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 hairline-b">
                <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-[var(--color-surface-2)] hairline">
                  {fileIcon(state.fileMime)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--color-ink)] truncate font-medium">
                    {state.fileName}
                  </p>
                  <p className="text-xs text-[var(--color-ink-muted)] mt-0.5">
                    {state.fileSize ? formatBytes(state.fileSize) : "—"} ·{" "}
                    {friendlyMime(state.fileMime ?? "")}
                  </p>
                </div>
                {finalStatus === "ready" && (
                  <span className="text-xs text-[var(--color-success)] font-medium">Pronto</span>
                )}
                {finalStatus === "quarantined" && (
                  <span className="text-xs text-[var(--color-danger)] font-medium">Quarentena</span>
                )}
              </div>

              <ol className="px-4 py-4 space-y-3">
                {PIPELINE.map((p, idx) => {
                  const status: "done" | "active" | "pending" =
                    idx < currentIdx || (idx === currentIdx && isDone && finalStatus === "ready")
                      ? "done"
                      : idx === currentIdx
                        ? "active"
                        : "pending";
                  return (
                    <li key={p.key} className="flex items-start gap-3">
                      <span
                        aria-hidden
                        className={cn(
                          "shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                          status === "done" &&
                            "bg-[var(--color-success-wash)] text-[var(--color-success)] border border-[color:color-mix(in_srgb,var(--color-success)_40%,transparent)]",
                          status === "active" &&
                            "bg-[var(--color-gold-wash)] text-[var(--color-gold)] border border-[var(--color-gold-dim)] pulse-gold",
                          status === "pending" &&
                            "border border-[var(--color-line)] text-[var(--color-ink-faint)]",
                        )}
                      >
                        {status === "done" ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : status === "active" ? (
                          <Spinner size="sm" className="text-[var(--color-gold)]" />
                        ) : (
                          idx + 1
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            "text-sm",
                            status === "done" && "text-[var(--color-ink-muted)]",
                            status === "active" && "text-[var(--color-ink)] font-medium",
                            status === "pending" && "text-[var(--color-ink-faint)]",
                          )}
                        >
                          {p.label}
                        </p>
                        <p className="text-xs text-[var(--color-ink-muted)] mt-0.5">
                          {p.description}
                        </p>
                        {p.key === "uploading" && status === "active" && (
                          <div className="mt-2 h-1.5 w-full rounded-full bg-[var(--color-canvas)] overflow-hidden">
                            <div
                              className="h-full bg-[var(--color-gold)] transition-all"
                              style={{ width: `${state.progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                      {p.key === "uploading" && status === "active" && (
                        <span className="text-mono text-xs text-[var(--color-gold-bright)] tabular-nums">
                          {state.progress}%
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>

              {finalStatus === "ready" && (
                <div className="hairline-t px-4 py-4 bg-[var(--color-success-wash)]/30 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <ShieldCheck
                      className="h-5 w-5 text-[var(--color-success)] shrink-0"
                      aria-hidden
                    />
                    <p className="text-sm text-[var(--color-ink)] truncate">
                      Arquivo enviado e disponível para o escritório.
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="secondary" size="sm" onClick={reset}>
                      Enviar outro
                    </Button>
                    <Button size="sm" asChild>
                      <Link to="/dashboard">Ver no painel</Link>
                    </Button>
                  </div>
                </div>
              )}

              {finalStatus === "quarantined" && (
                <div className="hairline-t px-4 py-4 bg-[var(--color-danger-wash)]/30 flex items-start gap-3">
                  <ShieldAlert
                    className="h-5 w-5 text-[var(--color-danger)] mt-0.5 shrink-0"
                    aria-hidden
                  />
                  <div className="flex-1">
                    <p className="text-sm text-[var(--color-ink)] font-medium">
                      Arquivo isolado por segurança
                    </p>
                    <p className="text-sm text-[var(--color-ink-muted)] mt-1">
                      Detectamos uma possível ameaça e o arquivo foi isolado. Ele não fica
                      disponível para o escritório.
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={reset} className="shrink-0">
                    Tentar outro
                  </Button>
                </div>
              )}

              {finalStatus === "rejected" && (
                <div className="hairline-t px-4 py-4 bg-[var(--color-danger-wash)]/30 flex items-start gap-3">
                  <AlertCircle
                    className="h-5 w-5 text-[var(--color-danger)] mt-0.5 shrink-0"
                    aria-hidden
                  />
                  <div className="flex-1">
                    <p className="text-sm text-[var(--color-ink)] font-medium">
                      Não foi possível concluir o envio
                    </p>
                    <p className="text-sm text-[var(--color-ink-muted)] mt-1">
                      O arquivo recebido não confere com o original. Tente enviar novamente.
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={reset} className="shrink-0">
                    Tentar de novo
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-md hairline bg-[var(--color-surface)] p-4">
            <p className="text-label text-[var(--color-ink-muted)]">Como funciona</p>
            <ul className="mt-3 space-y-3 text-sm text-[var(--color-ink-muted)]">
              <SideItem icon={<Hash className="h-3.5 w-3.5" />} title="Verificação de integridade">
                Garantimos que o arquivo chega exatamente como saiu do seu computador.
              </SideItem>
              <SideItem icon={<UploadCloud className="h-3.5 w-3.5" />} title="Envio direto">
                O arquivo vai do seu navegador direto para o armazenamento seguro do escritório.
              </SideItem>
              <SideItem icon={<ShieldCheck className="h-3.5 w-3.5" />} title="Análise de segurança">
                Todo arquivo passa por antivírus antes de ficar disponível para a equipe.
              </SideItem>
              <SideItem icon={<Hourglass className="h-3.5 w-3.5" />} title="Em segundos">
                Documentos típicos ficam prontos em &lt; 30s.
              </SideItem>
            </ul>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}

function SideItem({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-sm hairline bg-[var(--color-canvas)] text-[var(--color-gold)]">
        {icon}
      </span>
      <div>
        <p className="text-[var(--color-ink-2)] text-xs font-medium">{title}</p>
        <p className="text-xs leading-relaxed mt-0.5">{children}</p>
      </div>
    </li>
  );
}
